import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Download, Mail, MessageSquare, FileText, 
  Search, Filter, Calendar, TrendingUp, Users, DollarSign,
  FileSpreadsheet, RefreshCw, ChevronDown, Eye, Edit, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { format, parseISO } from 'date-fns';
import * as XLSX from 'xlsx';

const CampaignRegistrations = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editAmount, setEditAmount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [fieldLabels, setFieldLabels] = useState({});

  // Build field ID → label map from form config
  const getFieldLabel = (fieldId) => {
    return fieldLabels[fieldId] || fieldId.replace(/_/g, ' ');
  };

  // Format any form-data value for display (handles files, arrays, objects)
  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
    if (typeof value === 'object') {
      // File upload submitted from browser — File objects become {} after JSON
      // If the backend stored something with a url/name, show it nicely
      if (value.url) {
        return <a href={value.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{value.name || 'View file'}</a>;
      }
      if (value.name) return value.name;
      // Empty object (e.g. unserialized File) → fallback
      return Object.keys(value).length === 0 ? '(file)' : JSON.stringify(value);
    }
    return String(value);
  };

  // Check whether a form-data entry is editable as plain text in the Edit popup
  const isEditableValue = (value) =>
    value === null || value === undefined || typeof value === 'string' ||
    typeof value === 'number' || typeof value === 'boolean';

  // Helper function to extract registrant name from form_data
  const getRegistrantName = (formData) => {
    if (!formData || typeof formData !== 'object') return 'N/A';
    
    // Check for direct name field
    if (formData.name) return formData.name;
    if (formData.full_name) return formData.full_name;
    
    // Look for name type field (has _first, _last suffixes)
    const keys = Object.keys(formData);
    const firstNameKey = keys.find(k => k.endsWith('_first'));
    const lastNameKey = keys.find(k => k.endsWith('_last'));
    
    if (firstNameKey || lastNameKey) {
      const firstName = formData[firstNameKey] || '';
      const lastName = formData[lastNameKey] || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }
    
    // Look for a field value that's an object with first_name/last_name
    for (const key of keys) {
      const value = formData[key];
      if (value && typeof value === 'object' && (value.first_name || value.full_name)) {
        if (value.full_name) return value.full_name;
        const fn = value.first_name || '';
        const ln = value.last_name || '';
        return `${fn} ${ln}`.trim() || 'N/A';
      }
    }
    
    return 'N/A';
  };

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  useEffect(() => {
    filterRegistrations();
  }, [registrations, searchTerm, statusFilter, dateFilter]);

  const fetchData = async () => {
    try {
      const [campaignRes, registrationsRes, analyticsRes, formRes] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/campaigns/${campaignId}/registrations`),
        api.get(`/campaigns/${campaignId}/analytics`),
        api.get(`/campaigns/${campaignId}/form`).catch(() => ({ data: null }))
      ]);

      setCampaign(campaignRes.data);
      setRegistrations(registrationsRes.data);
      setAnalytics(analyticsRes.data);

      // Build field ID → label lookup from form config
      if (formRes.data?.fields) {
        const labels = {};
        formRes.data.fields.forEach(f => {
          if (f.id && f.label) labels[f.id] = f.label;
        });
        setFieldLabels(labels);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filterRegistrations = () => {
    let filtered = [...registrations];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(reg => {
        // Search in invoice number
        if (reg.invoice_number?.toLowerCase().includes(term)) return true;
        
        // Search in extracted name
        const name = getRegistrantName(reg.form_data);
        if (name.toLowerCase().includes(term)) return true;
        
        // Search in all form_data values (catches email, phone, etc.)
        const formValues = Object.values(reg.form_data || {});
        return formValues.some(v => 
          typeof v === 'string' && v.toLowerCase().includes(term)
        );
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(reg => reg.payment_status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(reg => {
        const regDate = new Date(reg.created_at);
        switch (dateFilter) {
          case 'today':
            return regDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
            return regDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
            return regDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    setFilteredRegistrations(filtered);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/campaigns/${campaignId}/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registrations_${campaign.slug}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Prepare data for Excel
      const data = filteredRegistrations.map(reg => {
        const row = {
          'Invoice Number': reg.invoice_number || '',
          'Registration ID': reg.id,
          'Payment Status': reg.payment_status,
          'Registration Status': reg.registration_status,
          'Total Amount': reg.total_amount,
          'Payment ID': reg.payment_id || '',
          'Created At': format(parseISO(reg.created_at), 'yyyy-MM-dd HH:mm:ss'),
        };

        // Add form data fields with proper labels
        Object.keys(reg.form_data || {}).forEach(key => {
          row[getFieldLabel(key)] = reg.form_data[key];
        });

        return row;
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Registrations');

      // Add analytics sheet
      const analyticsData = [
        { Metric: 'Total Registrations', Value: analytics?.total_registrations || 0 },
        { Metric: 'Paid Registrations', Value: analytics?.paid_registrations || 0 },
        { Metric: 'Pending Registrations', Value: analytics?.pending_registrations || 0 },
        { Metric: 'Total Revenue', Value: `₹${analytics?.total_revenue || 0}` },
        { Metric: 'Average Value', Value: `₹${(analytics?.average_registration_value || 0).toFixed(2)}` },
      ];
      const wsAnalytics = XLSX.utils.json_to_sheet(analyticsData);
      XLSX.utils.book_append_sheet(wb, wsAnalytics, 'Summary');

      // Download
      XLSX.writeFile(wb, `registrations_${campaign.slug}_${format(new Date(), 'yyyyMMdd')}.xlsx`);
      toast.success('Excel exported successfully!');
    } catch (error) {
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleResendReceipt = async (registrationId) => {
    try {
      await api.post(`/registrations/${registrationId}/resend-receipt`, {
        registration_id: registrationId
      });
      toast.success('Receipt resent successfully!');
    } catch (error) {
      toast.error('Failed to resend receipt');
    }
  };

  const handleSendWhatsApp = async (registrationId) => {
    try {
      await api.post(`/registrations/${registrationId}/send-whatsapp`);
      toast.success('WhatsApp notification sent!');
    } catch (error) {
      toast.error('Failed to send WhatsApp');
    }
  };

  // Handle Edit Registration
  const handleEditClick = (reg) => {
    setEditingRegistration(reg);
    setEditFormData({ ...reg.form_data });
    setEditAmount(reg.total_amount);
  };

  const handleEditFormChange = (key, value) => {
    setEditFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingRegistration) return;
    
    setSaving(true);
    try {
      const response = await api.put(`/registrations/${editingRegistration.id}`, {
        form_data: editFormData,
        total_amount: parseFloat(editAmount)
      });
      
      const result = response.data;
      
      // Show appropriate message based on payment adjustment
      if (result.refund_required) {
        toast.warning(
          `Registration updated. Refund of ₹${result.refund_amount.toFixed(2)} required. Super Admins and Directors have been notified.`,
          { duration: 6000 }
        );
      } else if (result.payment_link) {
        toast.success(
          `Registration updated. Additional payment link generated.`,
          { duration: 6000 }
        );
        // Optionally copy payment link to clipboard
        navigator.clipboard?.writeText(result.payment_link);
      } else {
        toast.success('Registration updated successfully');
      }
      
      // Refresh data
      await fetchData();
      setEditingRegistration(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update registration');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmRefund = async (registrationId) => {
    try {
      await api.post(`/registrations/${registrationId}/confirm-refund`);
      toast.success('Refund confirmed successfully');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm refund');
    }
  };

  // Prepare chart data from analytics
  const chartData = analytics?.registration_by_date?.map(item => ({
    date: format(parseISO(item.date), 'MMM dd'),
    count: item.count
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-jakarta">Loading registrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate(`/campaigns/${campaignId}`)}
                data-testid="back-button"
                className="hover:bg-slate-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-outfit font-bold text-slate-900">Registrations</h1>
                <p className="text-sm font-jakarta text-slate-600 mt-1">{campaign?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleExportCSV}
                disabled={exporting || filteredRegistrations.length === 0}
                variant="outline"
                data-testid="export-csv-button"
                className="border-slate-200 hover:bg-slate-50 font-jakarta font-medium rounded-lg"
              >
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button
                onClick={handleExportExcel}
                disabled={exporting || filteredRegistrations.length === 0}
                data-testid="export-excel-button"
                className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Excel'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-jakarta text-slate-500">Total</p>
                  <h3 className="text-2xl font-outfit font-bold text-slate-900">
                    {analytics.total_registrations}
                  </h3>
                </div>
              </div>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-jakarta text-slate-500">Paid</p>
                  <h3 className="text-2xl font-outfit font-bold text-green-600">
                    {analytics.paid_registrations}
                  </h3>
                </div>
              </div>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100">
                  <Calendar className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs font-jakarta text-slate-500">Pending</p>
                  <h3 className="text-2xl font-outfit font-bold text-yellow-600">
                    {analytics.pending_registrations}
                  </h3>
                </div>
              </div>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <DollarSign className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-xs font-jakarta text-slate-500">Revenue</p>
                  <h3 className="text-xl font-outfit font-bold text-slate-900">
                    ₹{(analytics.total_revenue * 0.9705).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </h3>
                </div>
              </div>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-accent/10">
                  <TrendingUp className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-xs font-jakarta text-slate-500">Avg Value</p>
                  <h3 className="text-xl font-outfit font-bold text-slate-900">
                    ₹{analytics.average_registration_value.toFixed(0)}
                  </h3>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Chart */}
        {chartData.length > 0 && (
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6 mb-6">
            <h2 className="text-lg font-outfit font-semibold text-slate-900 mb-4">Registration Trend</h2>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#7c3aed" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorCount)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Filters */}
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-4 mb-6">
          {/* Status Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-100">
            <span className="text-sm font-medium text-slate-600 mr-2">Filter:</span>
            {[
              { value: 'all', label: 'All', count: registrations.length },
              { value: 'completed', label: 'Paid', count: registrations.filter(r => r.payment_status === 'completed').length, color: 'green' },
              { value: 'pending', label: 'Pending', count: registrations.filter(r => r.payment_status === 'pending').length, color: 'yellow' },
              { value: 'failed', label: 'Failed', count: registrations.filter(r => r.payment_status === 'failed').length, color: 'red' }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  statusFilter === filter.value
                    ? filter.color === 'green' 
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : filter.color === 'yellow'
                      ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                      : filter.color === 'red'
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : 'bg-primary text-white'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {filter.label}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  statusFilter === filter.value
                    ? 'bg-white/30'
                    : 'bg-slate-200'
                }`}>
                  {filter.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search and Date Filter */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, phone, invoice..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>
            </div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            <div className="text-sm text-slate-500">
              Showing {filteredRegistrations.length} of {registrations.length}
            </div>
          </div>
        </Card>

        {/* Registrations Table */}
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-lg font-outfit font-semibold text-slate-900">All Registrations</h2>
          </div>

          {filteredRegistrations.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-jakarta">
                {registrations.length === 0 ? 'No registrations yet' : 'No matching registrations'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-jakarta font-semibold text-slate-600 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-jakarta font-semibold text-slate-600 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-jakarta font-semibold text-slate-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-jakarta font-semibold text-slate-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-jakarta font-semibold text-slate-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-jakarta font-semibold text-slate-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRegistrations.map((reg) => (
                    <tr key={reg.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-mono font-medium text-slate-900">{reg.invoice_number}</p>
                        <p className="text-xs text-slate-400">{reg.id.slice(0, 8)}...</p>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-jakarta font-medium text-slate-900">
                            {getRegistrantName(reg.form_data)}
                          </p>
                          <p className="text-xs font-jakarta text-slate-500">
                            {reg.form_data?.email || Object.values(reg.form_data || {}).find(v => typeof v === 'string' && v.includes('@')) || ''}
                          </p>
                          {(reg.form_data?.phone || Object.values(reg.form_data || {}).find(v => typeof v === 'string' && /^\d{10,}$/.test(v))) && (
                            <p className="text-xs font-jakarta text-slate-400">
                              {reg.form_data?.phone || Object.values(reg.form_data || {}).find(v => typeof v === 'string' && /^\d{10,}$/.test(v))}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-jakarta font-semibold text-slate-900">
                          ₹{reg.total_amount.toFixed(2)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-jakarta font-medium ${
                            reg.payment_status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : reg.payment_status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {reg.payment_status === 'completed' ? 'Paid' : reg.payment_status === 'pending' ? 'Pending' : 'Failed'}
                        </span>
                        {reg.refund_pending && (
                          <span className="ml-1 px-2 py-1 rounded-full text-xs font-jakarta font-medium bg-amber-100 text-amber-700">
                            Refund Pending
                          </span>
                        )}
                        {reg.additional_payment_required && (
                          <span className="ml-1 px-2 py-1 rounded-full text-xs font-jakarta font-medium bg-blue-100 text-blue-700">
                            +₹{reg.additional_payment_amount}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-jakarta text-slate-600">
                          {format(parseISO(reg.created_at), 'MMM dd, yyyy')}
                        </p>
                        <p className="text-xs text-slate-400">
                          {format(parseISO(reg.created_at), 'hh:mm a')}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedRegistration(reg)}
                            className="hover:bg-slate-100 rounded-lg h-8 w-8 p-0"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(reg)}
                            className="hover:bg-blue-100 rounded-lg h-8 w-8 p-0"
                            title="Edit Registration"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                          {reg.refund_pending && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleConfirmRefund(reg.id)}
                              className="hover:bg-green-100 rounded-lg h-8 w-8 p-0"
                              title="Confirm Refund"
                            >
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                          {reg.payment_status === 'completed' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResendReceipt(reg.id)}
                                className="hover:bg-slate-100 rounded-lg h-8 w-8 p-0"
                                title="Resend Receipt"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/registrations/${reg.id}/invoice`, '_blank')}
                                className="hover:bg-slate-100 rounded-lg h-8 w-8 p-0"
                                title="Download Invoice"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Registration Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-outfit font-bold">Registration Details</h2>
              <Button variant="ghost" size="sm" onClick={() => setSelectedRegistration(null)}>
                ✕
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-slate-500">Invoice Number</p>
                    <p className="font-mono font-semibold">{selectedRegistration.invoice_number}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedRegistration.payment_status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedRegistration.payment_status === 'completed' ? 'Paid' : 'Pending'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Amount</p>
                    <p className="font-semibold text-lg">₹{selectedRegistration.total_amount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Date</p>
                    <p className="font-medium">{format(parseISO(selectedRegistration.created_at), 'MMM dd, yyyy hh:mm a')}</p>
                  </div>
                </div>

                {selectedRegistration.payment_id && (
                  <div>
                    <p className="text-xs text-slate-500">Payment ID</p>
                    <p className="font-mono text-sm">{selectedRegistration.payment_id}</p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-sm font-semibold mb-3">Form Data</p>
                  <div className="space-y-2">
                    {Object.entries(selectedRegistration.form_data || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-1 border-b border-slate-100 gap-4">
                        <span className="text-sm text-slate-500">{getFieldLabel(key)}</span>
                        <span className="text-sm font-medium text-right break-words max-w-[60%]">
                          {formatFieldValue(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedRegistration(null)}>
                Close
              </Button>
              <Button onClick={() => {
                handleEditClick(selectedRegistration);
                setSelectedRegistration(null);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Registration Modal */}
      {editingRegistration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-outfit font-bold">Edit Registration</h2>
                <p className="text-sm text-slate-500">Invoice: {editingRegistration.invoice_number}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setEditingRegistration(null)}>
                ✕
              </Button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Warning about payment changes */}
              {editingRegistration.payment_status === 'completed' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Payment Already Completed</p>
                      <p className="text-xs mt-1">
                        If you change the amount:
                        <br />• <strong>Increase:</strong> A payment link for the difference will be generated
                        <br />• <strong>Decrease:</strong> Super Admins will be notified for refund
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Amount Field */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium text-slate-700">Total Amount (₹)</Label>
                <Input
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  className="mt-1 text-lg font-semibold"
                />
                {parseFloat(editAmount) !== editingRegistration.total_amount && (
                  <p className={`text-sm mt-2 ${parseFloat(editAmount) > editingRegistration.total_amount ? 'text-blue-600' : 'text-amber-600'}`}>
                    {parseFloat(editAmount) > editingRegistration.total_amount
                      ? `Additional payment of ₹${(parseFloat(editAmount) - editingRegistration.total_amount).toFixed(2)} will be required`
                      : `Refund of ₹${(editingRegistration.total_amount - parseFloat(editAmount)).toFixed(2)} will be required`
                    }
                  </p>
                )}
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-700">Form Data</p>
                {Object.entries(editFormData).map(([key, value]) => (
                  <div key={key}>
                    <Label className="text-xs text-slate-500">{getFieldLabel(key)}</Label>
                    {isEditableValue(value) ? (
                      <Input
                        value={value ?? ''}
                        onChange={(e) => handleEditFormChange(key, e.target.value)}
                        className="mt-1"
                      />
                    ) : (
                      <div className="mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">
                        {formatFieldValue(value)}
                        <span className="text-xs text-slate-400 ml-2">(read-only)</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingRegistration(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CampaignRegistrations;
